/**
 * Formula & Validation Rule Challenges
 * Each challenge has:
 *   - type: 'formula' | 'validation'
 *   - fields: available fields shown in the field picker
 *   - testCases: array of { record, priorRecord?, expected, label }
 *     For formula: expected = the value the formula should return
 *     For validation: expected = true (rule fires/blocks) or false (rule allows)
 */

export const FORMULA_CHALLENGES = [

  // ══════════════════════════════════════════════════════════════
  // FORMULA FIELD CHALLENGES
  // ══════════════════════════════════════════════════════════════

  {
    id: 'f001',
    type: 'formula',
    title: 'Opportunity Value Tier',
    difficulty: 'Easy',
    topic: 'Formula Fields',
    description: "Your sales ops team wants a text label on every Opportunity so reps can instantly see the deal size without reading a number. Deals over $100k are 'Enterprise', $10k–$100k are 'Mid-Market', and under $10k are 'SMB'. Write a formula field that returns the right label for every amount.",
    returnType: 'Text',
    fields: [
      { name: 'Amount', type: 'Currency', example: 150000 },
      { name: 'Name', type: 'Text', example: 'Acme Deal' },
    ],
    starterFormula: 'IF(Amount > 100000, "Enterprise",\n  IF(Amount >= 10000, "Mid-Market",\n    "SMB"\n  )\n)',
    concept: 'Nested IF chains are the Salesforce way to handle multiple tiers. Each IF checks one condition and either returns a value or hands off to the next IF.\n\nIF(condition1, result1,\n  IF(condition2, result2,\n    fallback\n  )\n)\n\nThe conditions are checked top to bottom — the first one that is TRUE wins. Always end with a fallback value, never leave a branch open.',
    docLink: 'https://help.salesforce.com/s/articleView?id=customize_functions_i.htm',
    testCases: [
      { label: 'Large enterprise deal', record: { Amount: 250000 }, expected: 'Enterprise' },
      { label: 'Mid-market deal', record: { Amount: 50000 }, expected: 'Mid-Market' },
      { label: 'Exactly $10k boundary', record: { Amount: 10000 }, expected: 'Mid-Market' },
      { label: 'Small SMB deal', record: { Amount: 5000 }, expected: 'SMB' },
      { label: 'Exactly $100k boundary', record: { Amount: 100000 }, expected: 'Mid-Market' },
    ],
  },

  {
    id: 'f002',
    type: 'formula',
    title: 'Full Name Builder',
    difficulty: 'Easy',
    topic: 'Formula Fields',
    description: "Your reporting team generates mail-merge letters and needs a single 'Full Name' field combining first and last name with exactly one space between them. The tricky part: some records only have a last name (no first name), and the formula must handle that without leaving a leading space.",
    returnType: 'Text',
    fields: [
      { name: 'FirstName', type: 'Text', example: 'John' },
      { name: 'LastName', type: 'Text', example: 'Smith' },
    ],
    starterFormula: 'IF(ISBLANK(FirstName),\n  LastName,\n  FirstName & " " & LastName\n)',
    concept: 'The & operator concatenates text in Salesforce formulas — it is the formula equivalent of + in most languages.\n\n"Hello" & " " & "World" → "Hello World"\n\nISBLANK() returns TRUE when a text field has no value. Always guard against blank before concatenating so you do not get leading or trailing spaces.\n\nCommon mistake: using + for text concatenation. In Salesforce formulas, + is for numbers only. & is for text.',
    docLink: 'https://help.salesforce.com/s/articleView?id=customize_functions_a.htm',
    testCases: [
      { label: 'Both names present', record: { FirstName: 'John', LastName: 'Smith' }, expected: 'John Smith' },
      { label: 'First name blank', record: { FirstName: '', LastName: 'Smith' }, expected: 'Smith' },
      { label: 'First name null', record: { FirstName: null, LastName: 'Dasari' }, expected: 'Dasari' },
      { label: 'Both names different', record: { FirstName: 'Priya', LastName: 'Patel' }, expected: 'Priya Patel' },
    ],
  },

  {
    id: 'f003',
    type: 'formula',
    title: 'Days Until Close',
    difficulty: 'Easy',
    topic: 'Formula Fields',
    description: "Your pipeline dashboard needs a 'Days Until Close' field so reps can see at a glance how urgent each opportunity is. The field should show how many days remain until the Close Date from today. If the opportunity is already past its close date, it should show a negative number (days overdue).",
    returnType: 'Number',
    fields: [
      { name: 'CloseDate', type: 'Date', example: '2026-08-15' },
    ],
    starterFormula: 'CloseDate - TODAY()',
    concept: 'Salesforce Date arithmetic: subtracting two dates gives you the number of days between them as a number.\n\nCloseDate - TODAY()  →  number of days until close\n\nIf CloseDate is in the past, the result is negative — which is useful for showing overdue deals.\n\nTODAY() returns the current date with no time component. Use NOW() only if you need the time as well.',
    docLink: 'https://help.salesforce.com/s/articleView?id=customize_functions_d.htm',
    testCases: [
      { label: 'Future close date', record: { CloseDate: getFutureDate(30) }, expected: 30 },
      { label: 'Close date is today', record: { CloseDate: getTodayDate() }, expected: 0 },
      { label: 'Past close date', record: { CloseDate: getFutureDate(-10) }, expected: -10 },
      { label: '90 days out', record: { CloseDate: getFutureDate(90) }, expected: 90 },
    ],
  },

  {
    id: 'f004',
    type: 'formula',
    title: 'Discount Price Calculator',
    difficulty: 'Easy',
    topic: 'Formula Fields',
    description: "Your pricing team wants a 'Discounted Amount' formula field that applies the discount percentage stored on the opportunity. If no discount is set, it should return the original amount. Round the result to 2 decimal places.",
    returnType: 'Currency',
    fields: [
      { name: 'Amount', type: 'Currency', example: 10000 },
      { name: 'Discount_Percent__c', type: 'Percent', example: 15 },
    ],
    starterFormula: 'ROUND(\n  Amount * (1 - Discount_Percent__c / 100),\n  2\n)',
    concept: 'Percent fields in Salesforce store the raw number (15 for 15%), not the decimal (0.15). So you divide by 100 to convert:\n\nAmount * (1 - Discount_Percent__c / 100)\n= 10000 * (1 - 15/100)\n= 10000 * 0.85\n= 8500\n\nROUND(number, decimal_places) prevents floating-point precision issues in currency calculations.\n\nCommon mistake: forgetting to divide by 100 and multiplying by 15 instead of 0.15.',
    docLink: 'https://help.salesforce.com/s/articleView?id=customize_functions_m.htm',
    testCases: [
      { label: '15% discount on $10k', record: { Amount: 10000, Discount_Percent__c: 15 }, expected: 8500 },
      { label: '0% discount', record: { Amount: 5000, Discount_Percent__c: 0 }, expected: 5000 },
      { label: '50% discount', record: { Amount: 8000, Discount_Percent__c: 50 }, expected: 4000 },
      { label: '10% discount with decimal', record: { Amount: 9999, Discount_Percent__c: 10 }, expected: 8999.10 },
    ],
  },

  {
    id: 'f005',
    type: 'formula',
    title: 'Commission Tier Calculator',
    difficulty: 'Medium',
    topic: 'Formula Fields',
    description: "Your compensation team uses a tiered commission model. Reps earn 5% on deals under $50k, 8% on deals $50k-$200k, and 12% on deals over $200k. But only Closed Won opportunities earn commission — all other stages return 0. Write a formula that calculates the commission amount.",
    returnType: 'Currency',
    fields: [
      { name: 'Amount', type: 'Currency', example: 75000 },
      { name: 'StageName', type: 'Picklist', example: 'Closed Won' },
    ],
    starterFormula: 'IF(ISPICKVAL(StageName, "Closed Won"),\n  IF(Amount > 200000, Amount * 0.12,\n    IF(Amount >= 50000, Amount * 0.08,\n      Amount * 0.05\n    )\n  ),\n  0\n)',
    concept: 'ISPICKVAL is the only way to compare picklist fields in Salesforce formulas. Using StageName = "Closed Won" does NOT work for picklists.\n\n// WRONG:\nIF(StageName = "Closed Won", ...)\n\n// CORRECT:\nIF(ISPICKVAL(StageName, "Closed Won"), ...)\n\nThe outer IF handles the stage gate, the inner IFs handle the tiers — each with its own rate.',
    docLink: 'https://help.salesforce.com/s/articleView?id=customize_functions_i.htm',
    testCases: [
      { label: 'Closed Won, high tier', record: { Amount: 300000, StageName: 'Closed Won' }, expected: 36000 },
      { label: 'Closed Won, mid tier', record: { Amount: 100000, StageName: 'Closed Won' }, expected: 8000 },
      { label: 'Closed Won, low tier', record: { Amount: 30000, StageName: 'Closed Won' }, expected: 1500 },
      { label: 'Prospecting — no commission', record: { Amount: 500000, StageName: 'Prospecting' }, expected: 0 },
      { label: 'Closed Lost — no commission', record: { Amount: 200000, StageName: 'Closed Lost' }, expected: 0 },
    ],
  },

  {
    id: 'f006',
    type: 'formula',
    title: 'Email Domain Extractor',
    difficulty: 'Medium',
    topic: 'Formula Fields',
    description: "Your marketing team wants to segment leads by email domain to identify which companies are engaging. Write a formula that extracts everything after the @ symbol from the Email field. For example, 'john@acme.com' should return 'acme.com'.",
    returnType: 'Text',
    fields: [
      { name: 'Email', type: 'Email', example: 'john.smith@acme.com' },
    ],
    starterFormula: 'MID(Email, FIND("@", Email) + 1, LEN(Email))',
    concept: 'FIND returns the position of a character in a string (1-indexed). Combine it with MID to extract everything after it:\n\nFIND("@", "john@acme.com")  →  5\nMID("john@acme.com", 5 + 1, 100)  →  "acme.com"\n\nUsing LEN(Email) as the length argument safely captures everything to the end regardless of domain length.\n\nCommon mistake: off-by-one errors. FIND gives you the position OF the @, so you need + 1 to start after it.',
    docLink: 'https://help.salesforce.com/s/articleView?id=customize_functions_m.htm',
    testCases: [
      { label: 'Standard email', record: { Email: 'john.smith@acme.com' }, expected: 'acme.com' },
      { label: 'Subdomain email', record: { Email: 'priya@mail.salesforce.com' }, expected: 'mail.salesforce.com' },
      { label: 'Short domain', record: { Email: 'x@a.io' }, expected: 'a.io' },
      { label: 'Different email', record: { Email: 'contact@trailcode.org' }, expected: 'trailcode.org' },
    ],
  },

  {
    id: 'f007',
    type: 'formula',
    title: 'Contact Completeness Score',
    difficulty: 'Medium',
    topic: 'Formula Fields',
    description: "Your data quality team wants a 0-100 score for each Contact showing how complete their record is. Score: Email present = 30 points, Phone present = 25 points, Title present = 20 points, Account linked = 15 points, Mailing address present = 10 points.",
    returnType: 'Number',
    fields: [
      { name: 'Email', type: 'Email', example: 'john@acme.com' },
      { name: 'Phone', type: 'Phone', example: '555-1234' },
      { name: 'Title', type: 'Text', example: 'VP Sales' },
      { name: 'AccountId', type: 'Lookup', example: '001xx000000abcd' },
      { name: 'MailingStreet', type: 'Text', example: '123 Main St' },
    ],
    starterFormula: 'IF(NOT(ISBLANK(Email)), 30, 0) +\nIF(NOT(ISBLANK(Phone)), 25, 0) +\nIF(NOT(ISBLANK(Title)), 20, 0) +\nIF(NOT(ISBLANK(AccountId)), 15, 0) +\nIF(NOT(ISBLANK(MailingStreet)), 10, 0)',
    concept: 'Additive scoring uses separate IF statements (NOT else-if) because each condition is independent — multiple can be true at once.\n\nIF(condition1, points, 0) +\nIF(condition2, points, 0) +\nIF(condition3, points, 0)\n\nEach IF adds its points or 0, and the + operators sum everything up. This is different from nested IFs (which are mutually exclusive).',
    docLink: 'https://help.salesforce.com/s/articleView?id=customize_functions_i.htm',
    testCases: [
      { label: 'Perfect score', record: { Email: 'j@a.com', Phone: '555', Title: 'VP', AccountId: '001', MailingStreet: '123 Main' }, expected: 100 },
      { label: 'Email only', record: { Email: 'j@a.com', Phone: null, Title: null, AccountId: null, MailingStreet: null }, expected: 30 },
      { label: 'Email + Phone', record: { Email: 'j@a.com', Phone: '555', Title: null, AccountId: null, MailingStreet: null }, expected: 55 },
      { label: 'Completely empty', record: { Email: null, Phone: null, Title: null, AccountId: null, MailingStreet: null }, expected: 0 },
      { label: 'All except email', record: { Email: null, Phone: '555', Title: 'Dir', AccountId: '001', MailingStreet: '1 St' }, expected: 70 },
    ],
  },

  {
    id: 'f008',
    type: 'formula',
    title: 'Fiscal Quarter Label',
    difficulty: 'Hard',
    topic: 'Formula Fields',
    description: "Your company's fiscal year starts in February. Write a formula that returns the fiscal quarter label ('Q1', 'Q2', 'Q3', 'Q4') for a given Close Date. Feb-Apr = Q1, May-Jul = Q2, Aug-Oct = Q3, Nov-Jan = Q4.",
    returnType: 'Text',
    fields: [
      { name: 'CloseDate', type: 'Date', example: '2026-03-15' },
    ],
    starterFormula: 'IF(MONTH(CloseDate) >= 2 && MONTH(CloseDate) <= 4, "Q1",\n  IF(MONTH(CloseDate) >= 5 && MONTH(CloseDate) <= 7, "Q2",\n    IF(MONTH(CloseDate) >= 8 && MONTH(CloseDate) <= 10, "Q3",\n      "Q4"\n    )\n  )\n)',
    concept: 'MONTH() extracts the month number (1-12) from a date. Combine with AND or && for range checks:\n\nMONTH(CloseDate) >= 2 && MONTH(CloseDate) <= 4\n\nIn Salesforce formula fields you use AND() or the && operator. The final ELSE maps to Q4 which covers Nov (11), Dec (12), and Jan (1) — the three months not captured by Q1-Q3.',
    docLink: 'https://help.salesforce.com/s/articleView?id=customize_functions_d.htm',
    testCases: [
      { label: 'February → Q1', record: { CloseDate: '2026-02-15' }, expected: 'Q1' },
      { label: 'April → Q1', record: { CloseDate: '2026-04-30' }, expected: 'Q1' },
      { label: 'May → Q2', record: { CloseDate: '2026-05-01' }, expected: 'Q2' },
      { label: 'September → Q3', record: { CloseDate: '2026-09-15' }, expected: 'Q3' },
      { label: 'November → Q4', record: { CloseDate: '2026-11-01' }, expected: 'Q4' },
      { label: 'January → Q4', record: { CloseDate: '2026-01-15' }, expected: 'Q4' },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  // VALIDATION RULE CHALLENGES
  // ══════════════════════════════════════════════════════════════

  {
    id: 'v001',
    type: 'validation',
    title: 'Require Close Date on Closed Won',
    difficulty: 'Easy',
    topic: 'Validation Rules',
    description: "Your sales ops team found 47 Closed Won opportunities with no Close Date, making pipeline reporting useless. Write a validation rule that prevents saving an opportunity as 'Closed Won' if the Close Date is blank.",
    errorMessage: 'Close Date is required when Stage is Closed Won.',
    fields: [
      { name: 'StageName', type: 'Picklist', example: 'Closed Won' },
      { name: 'CloseDate', type: 'Date', example: '2026-07-01' },
    ],
    starterFormula: 'AND(\n  ISPICKVAL(StageName, "Closed Won"),\n  ISBLANK(CloseDate)\n)',
    concept: 'Validation rules return TRUE to BLOCK a save, FALSE to ALLOW it. This feels backwards at first.\n\nThink of it as: "return TRUE when the data is INVALID"\n\nAND() chains two conditions — both must be true for the rule to fire:\n→ Stage IS Closed Won (ISPICKVAL)\n→ Close Date IS blank (ISBLANK)\n\nOnly when BOTH are true does the rule fire and block the save.',
    docLink: 'https://help.salesforce.com/s/articleView?id=fields_defining_field_validation_formulas.htm',
    testCases: [
      { label: 'Closed Won + no date → BLOCK', record: { StageName: 'Closed Won', CloseDate: null }, expected: true },
      { label: 'Closed Won + has date → ALLOW', record: { StageName: 'Closed Won', CloseDate: '2026-07-01' }, expected: false },
      { label: 'Prospecting + no date → ALLOW', record: { StageName: 'Prospecting', CloseDate: null }, expected: false },
      { label: 'Closed Lost + no date → ALLOW', record: { StageName: 'Closed Lost', CloseDate: null }, expected: false },
    ],
  },

  {
    id: 'v002',
    type: 'validation',
    title: 'Contact Must Have Email OR Phone',
    difficulty: 'Easy',
    topic: 'Validation Rules',
    description: "Your customer success team can't reach contacts who have neither an email address nor a phone number. Write a validation rule that blocks saving a Contact if BOTH Email and Phone are blank.",
    errorMessage: 'A Contact must have at least an Email or Phone number.',
    fields: [
      { name: 'Email', type: 'Email', example: 'john@acme.com' },
      { name: 'Phone', type: 'Phone', example: '555-1234' },
    ],
    starterFormula: 'AND(\n  ISBLANK(Email),\n  ISBLANK(Phone)\n)',
    concept: 'When you want to block if NEITHER condition is met, you AND the two ISBLANK checks:\n\nAND(ISBLANK(Email), ISBLANK(Phone))\n\nThis fires (blocks) only when BOTH are blank. If either has a value, one ISBLANK returns FALSE, AND returns FALSE, and the record saves.\n\nCommon mistake: using OR instead of AND — OR(ISBLANK(Email), ISBLANK(Phone)) would block any record missing EITHER field, which is too strict.',
    docLink: 'https://help.salesforce.com/s/articleView?id=fields_defining_field_validation_formulas.htm',
    testCases: [
      { label: 'Both blank → BLOCK', record: { Email: null, Phone: null }, expected: true },
      { label: 'Email only → ALLOW', record: { Email: 'j@a.com', Phone: null }, expected: false },
      { label: 'Phone only → ALLOW', record: { Email: null, Phone: '555-1234' }, expected: false },
      { label: 'Both present → ALLOW', record: { Email: 'j@a.com', Phone: '555' }, expected: false },
    ],
  },

  {
    id: 'v003',
    type: 'validation',
    title: 'Prevent Amount Decrease on Update',
    difficulty: 'Medium',
    topic: 'Validation Rules',
    description: "Your finance team discovered reps were quietly reducing opportunity amounts after verbal approval to hit their quota. Write a validation rule that prevents the Amount from being decreased on an existing record. New records can have any amount.",
    errorMessage: 'Opportunity Amount cannot be decreased. Contact your manager for approval.',
    fields: [
      { name: 'Amount', type: 'Currency', example: 50000 },
    ],
    starterFormula: 'AND(\n  NOT(ISNEW()),\n  Amount < PRIORVALUE(Amount)\n)',
    concept: 'PRIORVALUE() returns what the field contained BEFORE this edit — only available in validation rules, not formula fields.\n\nISNEW() returns TRUE when a record is being created (not updated). NOT(ISNEW()) means "this is an update".\n\nCombined:\nAND(\n  NOT(ISNEW()),           -- only on updates\n  Amount < PRIORVALUE(Amount)  -- and amount went down\n)\n\nThis fires only when an existing record is being saved with a lower amount.',
    docLink: 'https://help.salesforce.com/s/articleView?id=fields_defining_field_validation_formulas.htm',
    testCases: [
      { label: 'Amount decreased → BLOCK', record: { Amount: 40000 }, priorRecord: { Amount: 50000 }, expected: true },
      { label: 'Amount increased → ALLOW', record: { Amount: 60000 }, priorRecord: { Amount: 50000 }, expected: false },
      { label: 'Amount unchanged → ALLOW', record: { Amount: 50000 }, priorRecord: { Amount: 50000 }, expected: false },
      { label: 'New record → ALLOW', record: { Amount: 10000 }, priorRecord: null, expected: false },
    ],
  },

  {
    id: 'v004',
    type: 'validation',
    title: 'Phone Number Length Check',
    difficulty: 'Medium',
    topic: 'Validation Rules',
    description: "Your call center integration rejects phone numbers that aren't exactly 10 digits. Reps are entering numbers with dashes, spaces and country codes causing sync failures. Write a validation rule that blocks saving if the phone number (when present) isn't exactly 10 characters.",
    errorMessage: 'Phone number must be exactly 10 digits (no spaces or dashes).',
    fields: [
      { name: 'Phone', type: 'Phone', example: '5551234567' },
    ],
    starterFormula: 'AND(\n  NOT(ISBLANK(Phone)),\n  LEN(Phone) <> 10\n)',
    concept: 'LEN() returns the number of characters in a text field. Use it with comparison operators to enforce length constraints.\n\nThe rule should only fire when BOTH:\n1. Phone has a value (NOT ISBLANK) — blank phones are OK\n2. Length is not 10 characters (<> means "not equal")\n\nIf you omit the ISBLANK check, the rule would fire on every blank phone record too — blocking records that should be allowed.',
    docLink: 'https://help.salesforce.com/s/articleView?id=fields_defining_field_validation_formulas.htm',
    testCases: [
      { label: '10 digits → ALLOW', record: { Phone: '5551234567' }, expected: false },
      { label: 'Blank phone → ALLOW', record: { Phone: null }, expected: false },
      { label: 'Phone with dashes → BLOCK', record: { Phone: '555-123-456' }, expected: true },
      { label: '11 digits → BLOCK', record: { Phone: '15551234567' }, expected: true },
      { label: '9 digits → BLOCK', record: { Phone: '555123456' }, expected: true },
    ],
  },

  {
    id: 'v005',
    type: 'validation',
    title: 'Prevent Backdating Opportunities',
    difficulty: 'Medium',
    topic: 'Validation Rules',
    description: "Quarter-end pressure has led reps to backdate opportunities to hit historical targets. Write a validation rule that prevents setting a Close Date in the past (before today). The rule should apply on both new records and updates.",
    errorMessage: 'Close Date cannot be set in the past.',
    fields: [
      { name: 'CloseDate', type: 'Date', example: '2026-08-01' },
    ],
    starterFormula: 'CloseDate < TODAY()',
    concept: 'Comparing a date field to TODAY() is the cleanest way to check if a date is in the past.\n\nCloseDate < TODAY()  →  TRUE if CloseDate is yesterday or earlier\n\nThis is elegantly simple because Salesforce date arithmetic is native. The rule fires (returns TRUE) whenever CloseDate is before today, blocking the save.\n\nCommon mistake: using NOW() instead of TODAY(). NOW() includes time, which causes issues when comparing to date-only fields.',
    docLink: 'https://help.salesforce.com/s/articleView?id=fields_defining_field_validation_formulas.htm',
    testCases: [
      { label: 'Yesterday → BLOCK', record: { CloseDate: getFutureDate(-1) }, expected: true },
      { label: 'Last year → BLOCK', record: { CloseDate: getFutureDate(-365) }, expected: true },
      { label: 'Today → ALLOW', record: { CloseDate: getTodayDate() }, expected: false },
      { label: 'Tomorrow → ALLOW', record: { CloseDate: getFutureDate(1) }, expected: false },
      { label: 'Next year → ALLOW', record: { CloseDate: getFutureDate(365) }, expected: false },
    ],
  },

  {
    id: 'v006',
    type: 'validation',
    title: 'Lead Source Required for Large Deals',
    difficulty: 'Medium',
    topic: 'Validation Rules',
    description: "Marketing attribution breaks down when high-value deals have no Lead Source. Write a validation rule that requires Lead Source to be populated whenever an Opportunity amount is over $50,000.",
    errorMessage: 'Lead Source is required for opportunities over $50,000.',
    fields: [
      { name: 'Amount', type: 'Currency', example: 75000 },
      { name: 'LeadSource', type: 'Picklist', example: 'Web' },
    ],
    starterFormula: 'AND(\n  Amount > 50000,\n  ISBLANK(LeadSource)\n)',
    concept: 'Conditional required fields — fields that are only required under certain conditions — are a classic validation rule pattern.\n\nAND(condition_that_triggers, ISBLANK(field_that_must_exist))\n\nBoth must be true to block:\n→ Amount is over $50k\n→ LeadSource is blank\n\nIf amount is under $50k, the rule does not fire even with a blank LeadSource. If amount is over $50k but LeadSource is filled in, the rule does not fire either.',
    docLink: 'https://help.salesforce.com/s/articleView?id=fields_defining_field_validation_formulas.htm',
    testCases: [
      { label: 'Over $50k, no source → BLOCK', record: { Amount: 75000, LeadSource: null }, expected: true },
      { label: 'Over $50k, has source → ALLOW', record: { Amount: 75000, LeadSource: 'Web' }, expected: false },
      { label: 'Under $50k, no source → ALLOW', record: { Amount: 30000, LeadSource: null }, expected: false },
      { label: 'Exactly $50k, no source → ALLOW', record: { Amount: 50000, LeadSource: null }, expected: false },
      { label: 'Over $50k, different source → ALLOW', record: { Amount: 100000, LeadSource: 'Referral' }, expected: false },
    ],
  },

  {
    id: 'v007',
    type: 'validation',
    title: 'Case Cannot Reopen After Closure',
    difficulty: 'Hard',
    topic: 'Validation Rules',
    description: "Your support SLA metrics are being gamed: agents close cases to hit their KPI, then quietly reopen them. Write a validation rule that prevents a Case from being moved back to 'Open' or 'In Progress' once it has been 'Closed'. New cases are allowed any status.",
    errorMessage: 'A closed case cannot be reopened. Please create a new case.',
    fields: [
      { name: 'Status', type: 'Picklist', example: 'Closed' },
    ],
    starterFormula: 'AND(\n  NOT(ISNEW()),\n  ISPICKVAL(PRIORVALUE(Status), "Closed"),\n  NOT(ISPICKVAL(Status, "Closed"))\n)',
    concept: 'This rule combines three checks:\n1. NOT(ISNEW()) — only on updates, not new records\n2. ISPICKVAL(PRIORVALUE(Status), "Closed") — it WAS closed before this edit\n3. NOT(ISPICKVAL(Status, "Closed")) — it is NOT closed now (i.e., being reopened)\n\nAll three must be true to block. PRIORVALUE on a picklist field requires wrapping in ISPICKVAL — you cannot compare PRIORVALUE of a picklist with = directly.',
    docLink: 'https://help.salesforce.com/s/articleView?id=fields_defining_field_validation_formulas.htm',
    testCases: [
      { label: 'Closed → Open → BLOCK', record: { Status: 'Open' }, priorRecord: { Status: 'Closed' }, expected: true },
      { label: 'Closed → In Progress → BLOCK', record: { Status: 'In Progress' }, priorRecord: { Status: 'Closed' }, expected: true },
      { label: 'Open → Closed → ALLOW', record: { Status: 'Closed' }, priorRecord: { Status: 'Open' }, expected: false },
      { label: 'Closed stays Closed → ALLOW', record: { Status: 'Closed' }, priorRecord: { Status: 'Closed' }, expected: false },
      { label: 'New case → ALLOW', record: { Status: 'Open' }, priorRecord: null, expected: false },
    ],
  },

  {
    id: 'v008',
    type: 'validation',
    title: 'End Date Must Follow Start Date',
    difficulty: 'Easy',
    topic: 'Validation Rules',
    description: "Your contracts team keeps creating records where the End Date is before the Start Date — causing automated renewal calculations to fail. Write a validation rule that blocks saving when both dates are present and End Date is before or equal to Start Date.",
    errorMessage: 'Contract End Date must be after the Start Date.',
    fields: [
      { name: 'ContractStartDate', type: 'Date', example: '2026-01-01' },
      { name: 'ContractEndDate', type: 'Date', example: '2027-01-01' },
    ],
    starterFormula: 'AND(\n  NOT(ISBLANK(ContractStartDate)),\n  NOT(ISBLANK(ContractEndDate)),\n  ContractEndDate <= ContractStartDate\n)',
    concept: 'When validating a relationship between two fields, always guard for blank first — otherwise the rule might fire on records where one or both dates are not yet set.\n\nThe three-part AND:\n1. Start Date is not blank\n2. End Date is not blank\n3. End Date is NOT after Start Date (<=)\n\nUsing <= instead of < means a same-day start and end is also blocked (a zero-duration contract).',
    docLink: 'https://help.salesforce.com/s/articleView?id=fields_defining_field_validation_formulas.htm',
    testCases: [
      { label: 'End before start → BLOCK', record: { ContractStartDate: '2026-06-01', ContractEndDate: '2026-01-01' }, expected: true },
      { label: 'Same date → BLOCK', record: { ContractStartDate: '2026-06-01', ContractEndDate: '2026-06-01' }, expected: true },
      { label: 'End after start → ALLOW', record: { ContractStartDate: '2026-01-01', ContractEndDate: '2027-01-01' }, expected: false },
      { label: 'End date blank → ALLOW', record: { ContractStartDate: '2026-01-01', ContractEndDate: null }, expected: false },
      { label: 'Both blank → ALLOW', record: { ContractStartDate: null, ContractEndDate: null }, expected: false },
    ],
  },

  {
    id: 'v009',
    type: 'validation',
    title: 'Discount Approval Gate',
    difficulty: 'Hard',
    topic: 'Validation Rules',
    description: "Your pricing policy states that discounts over 20% require manager approval, tracked by a checkbox. Write a validation rule that blocks saving when the discount exceeds 20% but the 'Manager_Approved__c' checkbox is unchecked.",
    errorMessage: 'Discounts over 20% require manager approval. Check the Manager Approved box.',
    fields: [
      { name: 'Discount_Percent__c', type: 'Percent', example: 25 },
      { name: 'Manager_Approved__c', type: 'Checkbox', example: false },
    ],
    starterFormula: 'AND(\n  Discount_Percent__c > 20,\n  NOT(Manager_Approved__c)\n)',
    concept: 'Checkbox fields return TRUE or FALSE directly — no need for ISPICKVAL or ISBLANK. You can use them directly in AND/OR/NOT:\n\nNOT(Manager_Approved__c)  →  TRUE when checkbox is unchecked\n\nAND(\n  Discount_Percent__c > 20,  -- high discount\n  NOT(Manager_Approved__c)    -- not approved\n)\n\nThis fires when discount is high AND approval is missing. If either condition is false, the record saves.',
    docLink: 'https://help.salesforce.com/s/articleView?id=fields_defining_field_validation_formulas.htm',
    testCases: [
      { label: '25% discount, not approved → BLOCK', record: { Discount_Percent__c: 25, Manager_Approved__c: false }, expected: true },
      { label: '25% discount, approved → ALLOW', record: { Discount_Percent__c: 25, Manager_Approved__c: true }, expected: false },
      { label: '20% discount, not approved → ALLOW', record: { Discount_Percent__c: 20, Manager_Approved__c: false }, expected: false },
      { label: '0% discount, not approved → ALLOW', record: { Discount_Percent__c: 0, Manager_Approved__c: false }, expected: false },
      { label: '50% discount, approved → ALLOW', record: { Discount_Percent__c: 50, Manager_Approved__c: true }, expected: false },
    ],
  },

  {
    id: 'v010',
    type: 'validation',
    title: 'Multi-Field Dependency: High Priority Case',
    difficulty: 'Hard',
    topic: 'Validation Rules',
    description: "Your escalation policy requires that any Case marked 'High' priority must have: (1) a Subject of at least 10 characters, and (2) a Description. Write a single validation rule that enforces both requirements only for High priority cases.",
    errorMessage: 'High priority cases require a detailed Subject (10+ characters) and a Description.',
    fields: [
      { name: 'Priority', type: 'Picklist', example: 'High' },
      { name: 'Subject', type: 'Text', example: 'Login fails' },
      { name: 'Description', type: 'LongText', example: 'User cannot log in' },
    ],
    starterFormula: 'AND(\n  ISPICKVAL(Priority, "High"),\n  OR(\n    LEN(Subject) < 10,\n    ISBLANK(Description)\n  )\n)',
    concept: 'Complex multi-field validation nests AND and OR:\n\nAND(\n  trigger_condition,   -- only for High priority\n  OR(\n    field1_invalid,    -- subject too short\n    field2_invalid     -- description missing\n  )\n)\n\nThe outer AND means "this is a High priority case AND something is wrong". The inner OR means "at least one required field fails". If EITHER field fails for a High priority case, the rule fires.',
    docLink: 'https://help.salesforce.com/s/articleView?id=fields_defining_field_validation_formulas.htm',
    testCases: [
      { label: 'High, short subject → BLOCK', record: { Priority: 'High', Subject: 'Login', Description: 'User cannot log in' }, expected: true },
      { label: 'High, no description → BLOCK', record: { Priority: 'High', Subject: 'Login page fails', Description: null }, expected: true },
      { label: 'High, both missing → BLOCK', record: { Priority: 'High', Subject: 'Login', Description: null }, expected: true },
      { label: 'High, both valid → ALLOW', record: { Priority: 'High', Subject: 'Login page fails to load', Description: 'Cannot access' }, expected: false },
      { label: 'Low priority, both missing → ALLOW', record: { Priority: 'Low', Subject: 'Bug', Description: null }, expected: false },
    ],
  },
];

// ── Date helpers (used by test cases) ────────────────────────────────────────

function getFutureDate(daysFromNow) {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  return d.toISOString().split('T')[0];
}

function getTodayDate() {
  return new Date().toISOString().split('T')[0];
}